import { ErrorTypes } from '@fabric-ui/components';
import { useSnackbar } from 'notistack';
import { useNetworkStatus } from '../context/NetworkStatusContext';
import { getNetworkErrorCode } from '../utils/utils';

const abortedErrorsMessages = [
  'signal is aborted without reason',
  'The operation was aborted.',
  'The user aborted a request.',
  'Fetch is aborted',
];

export const UNKNOWN_ERROR_MESSAGE = 'Something went wrong, Our engineers have been notified';

export const ERROR_STATUS = {
  BAD_REQUEST: 400,
  CONFLICT: 409,
} as const;

export const serializeError = (error: Error) => ({
  ...error,
  message: error?.message,
  name: error?.name,
  stack: error?.stack,
});

export type ErrorSnackbarSuppressor = (error: unknown) => boolean;

const shouldSuppressError = (error: unknown, suppressor: ErrorSnackbarSuppressor): boolean => {
  try {
    return suppressor(error);
  } catch {
    return false;
  }
};

const filterSnackbarError = (error, suppressor?: ErrorSnackbarSuppressor): { snackbarError: any; allErrorsSuppressed: boolean } => {
  if (!suppressor) {
    return { snackbarError: error, allErrorsSuppressed: false };
  }

  if (Array.isArray(error)) {
    const errors = error.filter(e => !shouldSuppressError(e, suppressor));
    return {
      snackbarError: errors.length === error.length ? error : errors,
      allErrorsSuppressed: errors.length === 0,
    };
  }

  if (Array.isArray(error?.response?.errors)) {
    const errors = error.response.errors.filter(e => !shouldSuppressError(e, suppressor));
    return {
      snackbarError: errors.length === error.response.errors.length ? error : { ...error, response: { ...error.response, errors } },
      allErrorsSuppressed: errors.length === 0,
    };
  }

  const allErrorsSuppressed = shouldSuppressError(error, suppressor);
  return { snackbarError: allErrorsSuppressed ? [] : error, allErrorsSuppressed };
};

const getErrorMessage = error =>
  error?.response?.errors?.map(e => e?.message).join('\n\n') ??
  error?.message ??
  (Array.isArray(error) && error.map(e => e?.message || '').join(', ')) ??
  (typeof error === 'string' && error);

const checkTimeout = status => [502, 503, 504].includes(status);
export default function useHandleError(shouldShowError?) {
  const { enqueueSnackbar } = useSnackbar();
  const { isOnline } = useNetworkStatus();
  return (error?, others?, errorSnackbarSuppressor?: ErrorSnackbarSuppressor, snackbarErrorSource = error) => {
    const { snackbarError, allErrorsSuppressed } = filterSnackbarError(snackbarErrorSource, errorSnackbarSuppressor);
    const status = getNetworkErrorCode(error);
    const snackbarStatus = getNetworkErrorCode(snackbarError);
    const originalErrorMessage = getErrorMessage(error);
    const snackbarErrorMessage = getErrorMessage(snackbarError);

    if (abortedErrorsMessages.includes(originalErrorMessage?.toString()?.trim())) {
      return;
    }

    const isValidationError =
      [400, 406, 429].includes(status) ||
      error?.[0]?.extensions?.classification === 'BAD_REQUEST' ||
      error?.[0]?.extensions?.classification === 'ValidationError';
    const isSnackbarValidationError =
      [400, 406, 429].includes(snackbarStatus) ||
      snackbarError?.[0]?.extensions?.classification === 'BAD_REQUEST' ||
      snackbarError?.[0]?.extensions?.classification === 'ValidationError';

    if (!isOnline || !navigator.onLine) {
      console.warn({
        status: ErrorTypes.Offline,
        error: serializeError(error),
        ...others,
      });
      return;
    }

    console.error({
      status: isValidationError ? ErrorTypes.Validation : undefined,
      error: serializeError(error),
      ...others,
    });

    if (allErrorsSuppressed || abortedErrorsMessages.includes(snackbarErrorMessage?.toString()?.trim())) {
      return;
    }

    enqueueSnackbar(
      snackbarStatus === 403
        ? 'Access Denied'
        : checkTimeout(snackbarStatus)
          ? 'Request Timed out'
          : shouldShowError || isSnackbarValidationError
            ? snackbarErrorMessage
            : UNKNOWN_ERROR_MESSAGE,
      {
        variant: snackbarStatus === 429 ? 'warning' : 'error',
        style: { whiteSpace: 'pre-line' },
      }
    );
  };
}
